import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { withUser } from "@unifiedpatents/sso";
import LoadingWrapper from "../../common/loading-wrapper";
import { Graph } from "@cosmograph/cosmos";
import { getAssets } from "../../../services/api-landscape";
import { Button } from "antd";
import { PauseCircleOutlined, PlayCircleOutlined, FullscreenOutlined } from "@ant-design/icons";

const isIdArrEqual = (arr1, arr2) => {
  const s1 = new Set(arr1);
  const s2 = new Set(arr2);
  const union = new Set([...arr1, ...arr2]);
  return s1.size === s2.size && s1.size === union.size;
};

class CitationGraph extends Component {
  static propTypes = {
    standard: PropTypes.string.isRequired,
    nodesTop: PropTypes.arrayOf(PropTypes.string),
    nodesUser: PropTypes.arrayOf(PropTypes.string)
  };

  static defaultProps = {
    nodesTop: [],
    nodesUser: []
  };

  state = {
    nodes: [],
    edges: [],
    nodeSelected: null,
    loading: true
  };

  componentDidMount() {
    this._isMounted = true;
    this._getData();
  }

  componentDidUpdate(prevProps) {
    const nodesTopChanged = !isIdArrEqual(prevProps.nodesTop, this.props.nodesTop);
    const nodesUserChanged = !isIdArrEqual(prevProps.nodesUser, this.props.nodesUser);
    if ((nodesTopChanged || nodesUserChanged) && this.graph) {
      this.graph.setConfig({
        ...this.graph.config,
        ...this._getNodeConfig()
      });
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.graph.destroy();
  }

  render() {
    const { nodes, edges, nodeSelected, loading } = this.state;
    const { nodesTop } = this.props;
    return (
      <div style={{ position: "relative" }}>
        <LoadingWrapper loading={loading} style={{ minHeight: 800 }}>
          <canvas ref="graph" style={{ minHeight: 800 }} />
        </LoadingWrapper>
        {!loading && (
          <div style={{ minWidth: 250, position: "absolute", top: 0, left: 0 }}>
            <div>
              <h4>Overview</h4>
              <div>Patents: {nodes.length}</div>
              <div>Citations: {edges.length}</div>
              {nodeSelected && <code>Selected: {nodeSelected.id}</code>}
            </div>
            <div style={{ marginTop: 16 }}>
              <h4>Top PatentRank Patents</h4>
              {nodesTop.map(pubnum => (
                <div key={pubnum}>
                  <Button size="small" onClick={() => this.setState({ nodeSelected: { id: pubnum } }, () => this.graph.selectNodeById(pubnum, true))}>
                    {pubnum}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        {!loading && (
          <div style={{ minWidth: 200, textAlign: "right", position: "absolute", top: 0, right: 0 }}>
            <Button icon={<PauseCircleOutlined />} type="ghost" size="large" onClick={() => this.graph.pause()} />
            <Button icon={<PlayCircleOutlined />} type="ghost" size="large" onClick={() => this.graph.restart()} />
            <Button icon={<FullscreenOutlined />} type="ghost" size="large" onClick={() => this.graph.fitView()} />
          </div>
        )}
      </div>
    );
  }

  _renderGraph = () => {
    const { nodes, edges } = this.state;
    if (nodes.length && edges.length) {
      const config = {
        simulation: {
          gravity: 0.25,
          repulsion: 1.5
        },
        renderLinks: false,
        backgroundColor: "#fff",
        nodeGreyoutOpacity: 0.02,
        ...this._getNodeConfig(),
        events: {
          onClick: (node, i) => {
            if (node && i !== undefined) {
              this.graph.selectNodeByIndex(i, true);
              this.graph.zoomToNodeByIndex(i, 700, 1);
              this.setState({ nodeSelected: node });
            } else {
              this.graph.unselectNodes();
              this.setState({ nodeSelected: null });
            }
          }
        }
      };
      this.graph = new Graph(this.refs.graph, config);
      this.graph.setZoomLevel(0.2);
      this.graph.setData(nodes, edges);
    }
  };

  _getData = () => {
    const { standard } = this.props;
    return fetch(`//public-assets.unifiedpatents.com/opal-subgraph/subgraph_${standard}.csv`)
      .then(res => res.text())
      .then(csv => {
        const { nodes, edges } = this._csvToNodesEdges(csv);
        if (this._isMounted)
          this.setState(
            {
              nodes,
              edges,
              loading: false
            },
            this._renderGraph
          );
      });
  };

  _getNodeConfig = () => {
    const { nodesTop, nodesUser } = this.props;
    const sTop = new Set(nodesTop);
    const sUser = new Set(nodesUser);
    return {
      nodeColor: node => (sTop.has(node.id) ? "#d9363680" : sUser.has(node.id) ? "#04cc3980" : "#1890FF40"),
      nodeSize: node => (sTop.has(node.id) ? 75 : sUser.has(node.id) ? 30 : Math.log(node.d + 1) * 5 + 1)
    };
  };

  _csvToNodesEdges = csv => {
    const nodesDict = {};
    const edges = [];
    const lines = csv.split("\n");
    lines.slice(1).forEach(line => {
      const [source, target] = line.split(",");
      if (!nodesDict[source]) nodesDict[source] = { id: source, d: 0 };
      if (!nodesDict[target]) {
        nodesDict[target] = { id: target, d: 1 };
      } else {
        nodesDict[target].d++;
      }
      edges.push({ source, target });
    });
    return {
      nodes: Object.values(nodesDict),
      edges
    };
  };
}

class CitationAnalysis extends Component {
  static propTypes = {
    esUrl: PropTypes.string.isRequired,
    version: PropTypes.string.isRequired,
    filter: PropTypes.object
  };

  static defaultProps = {
    filter: {}
  };

  state = {
    nodeIdsTop: [],
    nodeIdsUser: [],
    loading: true
  };

  componentDidMount() {
    this._isMounted = true;
    this._getData();
  }

  componentDidUpdate(prevProps) {
    const filterChanged = JSON.stringify(prevProps.filter) !== JSON.stringify(this.props.filter);
    if (filterChanged) {
      this._getData();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    const { nodeIdsTop, nodeIdsUser, loading } = this.state;
    const standard = this.props.version.split("_")[0];
    return (
      <Fragment>
        {loading && <LoadingWrapper loading={loading} />}
        <CitationGraph standard={standard} nodesTop={nodeIdsTop} nodesUser={nodeIdsUser} />
      </Fragment>
    );
  }

  _getTopNodes = () => {
    // retrieve patents with highest pagerank score from ES, regardless of user filters
    const { esUrl, version } = this.props;
    const query = { limit: 10, sort: "-pagerank" };
    return getAssets(esUrl, version, query, true).then(res => res.hits.hits.map(hit => hit._id));
  };

  _getUserNodes = () => {
    // retrieve patents filtered by users, sorted descendingly by similarity score
    const { esUrl, version, filter } = this.props;
    const query = { ...filter, limit: 10000, sort: "-pagerank" };
    return getAssets(esUrl, version, query, true).then(res => res.hits.hits.map(hit => hit._id));
  };

  _getData = () => {
    this.setState({ loading: true });
    return this._getTopNodes().then(pubnums => {
      return this._getUserNodes().then(pubnumsUser => {
        if (this._isMounted)
          this.setState({
            nodeIdsTop: pubnums,
            nodeIdsUser: pubnumsUser,
            loading: false
          });
      });
    });
  };
}

export default withUser(CitationAnalysis);
